import { routesMap } from 'app/Router';
import { Dispatch } from 'app/store';
import GalleryLabelDefaultImg from 'assets/img/gallery-label-default.png';
import { GallerySku, SkuGalleryLabelCount } from 'domains/gallerySkuDomain';
import { JobConfig } from 'domains/jobconfigDomain';
import { Contact } from 'domains/orgDomain';
import { ProjectJobAddFromEnum } from 'domains/projectDomain';
import { sortBy } from 'lodash-es';
import jobconfigService from 'services/jobconfigService';
import { withCanvaCategory } from './canvaMatchView';
import { getCanvaSessionDraftId, isCanvaSession } from './canvaSession';
import {
  UPLOAD_FILE_ADDRESS_PREFIX,
  UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX,
} from './constants';

export const startJobConfigWithGallerySku = async (
  dispatch: Dispatch,
  sku: GallerySku,
  expertMode: boolean,
  history: {
    push: (path: string) => void;
    replace: (path: string) => void;
  },
  replace?: boolean,
  jobProjectId?: string
) => {
  try {
    let configItem: JobConfig;
    // start job config directly
    if (jobProjectId) {
      const projectJobDetail = await dispatch.project.jobProjectAddJob({
        projectId: jobProjectId,
        addFrom: ProjectJobAddFromEnum.SHOP_DROP_DOWN,
        startParams: {
          skuId: sku.id,
          quantity: sku.defaultQty,
          isExpert: expertMode,
        },
      });
      configItem = await jobconfigService.getJobConfigFromCache(
        projectJobDetail.jobConfigId
      );
    } else {
      configItem = await dispatch.jobConfig.startJobConfig({
        skuId: sku.id,
        quantity: sku.defaultQty,
        isExpert: expertMode,
      });
    }

    const skuDetail = await dispatch.sku.startViewSku(sku.id);

    if (history) {
      if (expertMode) {
        (replace ? history.replace : history.push)(
          routesMap.jobConfig.path + '/expert/' + configItem.id
        );
      } else {
        (replace ? history.replace : history.push)(
          routesMap.jobConfig.path + '/' + configItem.id
        );
      }
    }

    dispatch.gallery.setState({
      selectedBusinessType: null,
    });
    dispatch.jobConfig.reset();
    dispatch.jobConfig.setState({
      configItem: configItem,
      sku: skuDetail,
      expertMode: !!expertMode,
      step: 1,
      calVisible: true,
    });
    return true;
  } catch {
    return false;
  }
};

export const startJobConfigWithGalleryLabel = (
  dispatch: Dispatch,
  galleryLabel: SkuGalleryLabelCount,
  history?: {
    push: (path: string) => void;
  }
) => {
  // A buyer carrying a parked Canva design never wants this wizard: it asks for the type, size,
  // page count and options the design has already settled, and it ends in the ordinary job
  // config rather than the expert one a publish produces. Send them to the product picker for
  // the same category instead -- it publishes on the pick, and publishes without asking when
  // the category holds a single product.
  //
  // Done here rather than at each link because the shop reaches this from the gallery cards,
  // the category strip, the header's Shop menu and the project shop panel alike.
  const canvaDraftId = isCanvaSession() ? getCanvaSessionDraftId() : undefined;
  if (canvaDraftId && history) {
    history.push(
      `${routesMap.gallery.path}${withCanvaCategory(
        `?canvaDraftId=${encodeURIComponent(canvaDraftId)}`,
        galleryLabel.skuLabel.id
      )}`
    );
    return;
  }
  if (history) {
    history.push(
      routesMap.jobConfig.path + `/label/${galleryLabel.skuLabel.id}`
    );
  }
  dispatch.gallery.setState({
    selectedBusinessType: null,
  });
  dispatch.jobConfig.reset();
  dispatch.jobConfig.setState({
    galleryLabel: { ...galleryLabel },
    curSelectedDropdownLabel: null,
    curSelectedGalleryLabel: null,
    curSelectedGallerySku: null,
    step: 0,
  });
};

export const getImage = (path: string, defaultPath?: string) => {
  if (path) {
    const filePath =
      path?.split('.').pop()?.toLowerCase() === 'pdf'
        ? `${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${path}`
        : `${UPLOAD_FILE_ADDRESS_PREFIX}${path}`;
    return filePath;
  } else {
    return defaultPath || GalleryLabelDefaultImg;
  }
};

export const sortContacts = (contacts: Contact[]): Contact[] => {
  return sortBy(contacts || [], (o) =>
    `${o.firstName} ${o.lastName}`.slice(0, 1).toUpperCase()
  );
};
